import { useEffect, useCallback, useRef } from "react";

/**
 * Custom hook for implementing infinite scroll functionality
 * @param {Function} loadMore - Function to call when more data needs to be loaded
 * @param {boolean} hasMore - Whether there's more data to load
 * @param {boolean} isLoading - Whether data is currently being loaded
 * @param {number} threshold - Distance from bottom (in pixels) to trigger load more (default: 100)
 * @returns {Function} ref function to attach to scrollable container
 */
export const useInfiniteScroll = (
  loadMore,
  hasMore,
  isLoading,
  threshold = 100,
) => {
  const containerRef = useRef(null);

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container || isLoading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    if (distanceFromBottom <= threshold) {
      loadMore();
    }
  }, [loadMore, hasMore, isLoading, threshold]);

  // No dependency list, on purpose: the container can be replaced between renders (a page that
  // switches between a grid and a list mounts a new scroll container), and the ref changing does
  // not re-render. Attaching again after every render keeps the listener on the live container.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  });

  return containerRef;
};

export default useInfiniteScroll;
